import { useEffect, useRef, useState } from "react";

const FIELD_CLASS =
  "w-full rounded bg-transparent caret-black py-0 text-xs text-gray-800 outline-none";

/** Selects keep a small inset so the closed value lines up with the header. */
const SELECT_CLASS = `${FIELD_CLASS} min-w-0 max-w-full border border-transparent bg-transparent px-1 focus:bg-transparent`;

/**
 * Text fields sit flush with the column header. Focus color comes from the
 * cell behind the field, so the control itself stays transparent.
 */
const TEXT_FIELD_CLASS = `${FIELD_CLASS} min-w-0 max-w-full border-0 bg-transparent px-0 focus:bg-transparent`;

/**
 * True when two text values match after trim.
 *
 * @param {*} left - Current stored value.
 * @param {*} right - Edited value.
 * @returns {boolean}
 */
function sameText(left, right) {
  return String(left ?? "").trim() === String(right ?? "").trim();
}

/**
 * True when two numeric values match, treating blank as empty.
 *
 * @param {*} left - Current stored value.
 * @param {*} right - Edited value.
 * @returns {boolean}
 */
function sameNumber(left, right) {
  const leftEmpty = left === null || left === undefined || left === "";
  const rightEmpty = right === null || right === undefined || right === "";
  if (leftEmpty || rightEmpty) {
    return leftEmpty && rightEmpty;
  }
  return Number(left) === Number(right);
}

/**
 * Parse a decimal input. Blank becomes null. Invalid text becomes undefined.
 *
 * @param {string} raw - Input text.
 * @returns {number|null|undefined}
 */
function parseDecimal(raw) {
  const text = String(raw ?? "").trim();
  if (!text) return null;
  const value = Number(text);
  if (Number.isNaN(value)) return undefined;
  return value;
}

/**
 * Full text a truncated cell could put in a tooltip.
 *
 * @param {Object} column - Column config.
 * @param {*} value - Current cell value.
 * @param {*} draft - In-progress text draft.
 * @returns {string}
 */
function overflowTitleText(column, value, draft) {
  if (!column.truncate) return "";
  if (column.type === "select") {
    const match = (column.options || []).find(
      (option) => String(option.value) === String(value ?? "")
    );
    return match?.label || "";
  }
  if (column.type === "readonly") {
    if (typeof value === "string" || typeof value === "number") return String(value);
    return "";
  }
  if (column.type === "text" || column.type === "number") return String(draft ?? "");
  return "";
}

/**
 * Native tooltip only while the measured element is clipping its text.
 *
 * @param {string} text - Full text.
 * @returns {{ ref: React.RefObject, title: string|undefined }}
 */
function useOverflowTitle(text) {
  const ref = useRef(null);
  const [title, setTitle] = useState(undefined);

  useEffect(() => {
    const element = ref.current;
    if (!element || !text) {
      setTitle(undefined);
      return undefined;
    }

    /**
     * Keep the tooltip only when the text does not fit in the box.
     *
     * @returns {void}
     */
    const update = () => {
      const clipped = element.scrollWidth > element.clientWidth + 1;
      setTitle(clipped ? text : undefined);
    };

    update();
    const observer = new ResizeObserver(update);
    observer.observe(element);
    return () => observer.disconnect();
  }, [text]);

  return { ref, title };
}

/**
 * Inline text, number, select, checkbox, or read-only cell.
 *
 * Text and number commit on blur. Select and checkbox commit when the value
 * changes, because that choice is finished as soon as it is made.
 *
 * @param {Object} props - Component props.
 * @param {Object} props.column - Column config.
 * @param {*} props.value - Current cell value.
 * @param {boolean} [props.disabled=false] - Disable editing while a save is in flight.
 * @param {boolean} [props.autoFocus=false] - Focus the control on mount.
 * @param {(nextValue: *) => void} props.onCommit - Called with the edited value.
 * @param {() => void} [props.onEscape] - Optional escape handler for a draft row.
 * @returns {JSX.Element}
 */
export default function ManagementCell({
  column,
  value,
  disabled = false,
  autoFocus = false,
  onCommit,
  onEscape,
}) {
  const [draft, setDraft] = useState(value ?? "");
  const overflow = useOverflowTitle(overflowTitleText(column, value, draft));

  useEffect(() => {
    setDraft(value ?? "");
  }, [value]);

  if (column.type === "readonly") {
    const text = value ?? "";
    return (
      <span
        ref={overflow.ref}
        className={`block px-1 ${column.truncate ? "truncate" : ""}`}
        title={overflow.title}
      >
        {text}
      </span>
    );
  }

  if (column.type === "checkbox") {
    return (
      <input
        type="checkbox"
        className="h-3.5 w-3.5 rounded border-gray-300 text-green-900 focus:ring-green-900"
        checked={Boolean(value)}
        disabled={disabled || column.editable === false}
        onChange={(event) => {
          if (Boolean(value) === event.target.checked) return;
          onCommit(event.target.checked);
        }}
      />
    );
  }

  if (column.type === "select") {
    return (
      <select
        ref={overflow.ref}
        className={`${SELECT_CLASS} ${column.truncate ? "truncate" : ""}`}
        title={overflow.title}
        value={value ?? ""}
        disabled={disabled || column.editable === false}
        autoFocus={autoFocus}
        onChange={(event) => {
          if (String(value ?? "") === event.target.value) return;
          onCommit(event.target.value);
        }}
      >
        {(column.options || []).map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    );
  }

  /**
   * Commit the current draft when it differs from the stored value.
   *
   * @returns {void}
   */
  const commitDraft = () => {
    if (column.type === "number") {
      const parsed = parseDecimal(draft);
      if (parsed === undefined || sameNumber(value, parsed)) {
        setDraft(value ?? "");
        return;
      }
      onCommit(parsed);
      return;
    }

    const next = String(draft ?? "").trim();
    if (sameText(value, next)) {
      setDraft(value ?? "");
      return;
    }
    onCommit(next);
  };

  /**
   * Blur on Enter for single-line fields. Escape restores or cancels a draft.
   *
   * @param {React.KeyboardEvent} event - Keyboard event.
   * @returns {void}
   */
  const handleKeyDown = (event) => {
    if (event.key === "Escape") {
      event.preventDefault();
      if (onEscape) {
        onEscape();
        return;
      }
      setDraft(value ?? "");
      event.currentTarget.blur();
      return;
    }
    if (event.key === "Enter" && column.type !== "longtext") {
      event.currentTarget.blur();
    }
  };

  if (column.type === "longtext") {
    return (
      <textarea
        rows={1}
        className={`${TEXT_FIELD_CLASS} resize-y`}
        value={draft}
        disabled={disabled || column.editable === false}
        placeholder={column.placeholder || ""}
        autoFocus={autoFocus}
        onChange={(event) => setDraft(event.target.value)}
        onBlur={commitDraft}
        onKeyDown={handleKeyDown}
      />
    );
  }

  return (
    <input
      type="text"
      inputMode={column.type === "number" ? "decimal" : undefined}
      ref={overflow.ref}
      className={`${TEXT_FIELD_CLASS} ${column.truncate ? "truncate" : ""} ${column.align === "right" ? "text-right" : ""}`}
      title={overflow.title}
      value={draft}
      disabled={disabled || column.editable === false}
      placeholder={column.placeholder || ""}
      autoFocus={autoFocus}
      onChange={(event) => setDraft(event.target.value)}
      onBlur={commitDraft}
      onKeyDown={handleKeyDown}
    />
  );
}
